import { Component, EventEmitter, Input, Output } from '@angular/core';
import { OptionsStoreService, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';
import { CustomerSpaceUser } from '@core/core.state';
import { TranslateService } from '@ngx-translate/core';
import { UsersTableBaseComponent } from '@apps/file-process/process/process-access-users-table/users-table-component-base';
import { ApplicationPermissionItem, CustomerSpaceUserModel } from '../customer-space-user.model';
import { appPermissionsMap } from '../utils/cs-user.utils';
import { CustomerSpaceApp, getPermissionForApp } from '../app-permission-helper';

@Component({
  selector: 'app-customer-space-users-table',
  templateUrl: './customer-space-users-table.component.html',
  styleUrls: ['./customer-space-users-table.component.scss'],
  standalone: false,
})
export class CustomerSpaceUsersTableComponent extends UsersTableBaseComponent<CustomerSpaceUser> {
  @Input() isCustomerSpaceActive!: boolean;
  @Input() customerSpaceId: string;
  @Input() titleTranslateKey: string;

  @Output() deleteEmployeeClicked = new EventEmitter<CustomerSpaceUser>();
  @Output() editEmployeeClicked = new EventEmitter<CustomerSpaceUser>();
  @Output() addEmployeeClicked = new EventEmitter();

  constructor(_optionsStoreService: OptionsStoreService, translateService: TranslateService) {
    super(_optionsStoreService, translateService);
  }

  override get uiTabSortingStorageKey(): UiTableKeys {
    return UiTableKeys.CustomerSpaceUsers;
  }

  override getTranslatedRoleName(user: CustomerSpaceUser): string {
    const roleName = this.translate(`roles.${user.customerSpaceRole.name.toLocaleLowerCase()}`);
    const deputy = user.isDeputy ? ` (${this.translate('users.user-profile.deputy')})` : '';

    return roleName + deputy;
  }

  override mapUser(user: CustomerSpaceUser): CustomerSpaceUserModel {
    const res = {
      id: user.id,
      userName: {
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.customerSpaceRole?.name,
        roleText: this.getTranslatedRoleName2(user.customerSpaceRole.name),
        isContact: user.isContact,
        isDeputy: user.isDeputy,
        deputyFromName: user.deputyFromName,
        contactText: user.isContact ? this.translate('customer-spaces.user-management.contact') : '',
        deputyText: user.isDeputy ? this.getTranslatedDeputy() : '',
      },
      account: {
        isActive: user.isActive,
        isInternal: user.isInternal,
      },
      applications: this.mapApplicationsForUser(user),
      organizationUnit: user.organizationUnit,
      _original: user,
    } as CustomerSpaceUserModel;
    return res;
  }

  mapApplicationsForUser(user: CustomerSpaceUser): ApplicationPermissionItem[] {
    if (!this.isCustomerSpaceActive) {
      return [];
    }

    const translateKeyPrefix = 'customer-spaces.user-management.app-permissions';

    // if user is 'CustomerSpaceAdmin' he has access to all permissions
    const allApplications = Object.entries(appPermissionsMap).map(
      ([key, value]) =>
        ({
          key,
          enabled: this.doesUserHaveAppPermission(user, key as CustomerSpaceApp),
          text: this.translate(`${translateKeyPrefix}.${value.translKey}`),
        }) as ApplicationPermissionItem,
    );

    return allApplications.filter((a) => a.enabled);
  }

  // duplicate fn
  public doesUserHaveAppPermission(user: CustomerSpaceUser, app: CustomerSpaceApp) {
    if (!user) {
      return false;
    }

    if (user.customerSpaceRole?.name === 'CustomerSpaceAdmin') {
      return true;
    }

    const appPermission = getPermissionForApp(app, this.customerSpaceId);

    return user.appPermissions.indexOf(appPermission) >= 0;
  }

  deleteEmployeeInternal(row: CustomerSpaceUserModel) {
    this.deleteEmployeeClicked.emit(this.toCustomerSpaceUser(row));
  }

  openEditEmployeeDialogInternal(row: CustomerSpaceUserModel) {
    this.editEmployeeClicked.emit(this.toCustomerSpaceUser(row));
  }
}
